import type { Candidate } from './templatePipeline';
import { templatesApi } from './api';

export interface Prediction {
  id: string;
  prediction: 'SLOT' | 'DECOR';
  confidence: number;
  reasoning: string;
}

const TIMEOUT_MS = 25000;
// Dense collage templates carry 12-20+ candidates; sending them ALL in one
// request routinely blew up (oversized payload / one huge Gemini call → 502 →
// zero predictions → the review pane showed "0 emplacement" and the save was
// impossible). Chunking bounds each request; one failed chunk only loses ITS
// candidates' predictions instead of the whole template's.
const CHUNK_SIZE = 6;

async function classifyChunk(chunk: Candidate[]): Promise<Prediction[]> {
  const sentIds = new Set(chunk.map((c) => c.id));
  const apiPromise = templatesApi.classifyCandidates(
    chunk.map((c) => ({ id: c.id, thumbnailDataUrl: c.thumbnailDataUrl })),
  );

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('classifyCandidates: timeout')), TIMEOUT_MS);
  });

  try {
    const response = await Promise.race([apiPromise, timeoutPromise]);
    if (!response || typeof response !== 'object') return [];
    const results = (response as { results?: unknown }).results;
    if (!Array.isArray(results)) return [];

    // Filter to ids we actually sent. Each result must have the right shape.
    const validated: Prediction[] = [];
    for (const r of results) {
      if (!r || typeof r !== 'object') continue;
      const p = r as Record<string, unknown>;
      if (typeof p.id !== 'string' || !sentIds.has(p.id)) continue;
      if (p.prediction !== 'SLOT' && p.prediction !== 'DECOR') continue;
      if (typeof p.confidence !== 'number') continue;
      validated.push({
        id: p.id,
        prediction: p.prediction,
        confidence: p.confidence,
        reasoning: typeof p.reasoning === 'string' ? p.reasoning : '',
      });
    }
    return validated;
  } catch {
    return [];
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function classifyCandidates(candidates: Candidate[]): Promise<Prediction[]> {
  if (candidates.length === 0) return [];
  const chunks: Candidate[][] = [];
  for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
    chunks.push(candidates.slice(i, i + CHUNK_SIZE));
  }
  // Sequential — parallel chunks would recreate the burst the worker/Gemini
  // struggles with. A failed chunk gets ONE retry before being skipped.
  const all: Prediction[] = [];
  for (const chunk of chunks) {
    let preds = await classifyChunk(chunk);
    if (preds.length === 0 && chunk.length > 0) preds = await classifyChunk(chunk);
    all.push(...preds);
  }
  return all;
}
